import React, { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import Modal from '../../../../components/Modal';
import Field from '../../../../components/Field';
import TextArea from '../../../../components/TextArea';
import SegmentedControl from '../../../../components/SegmentedControl';
import Dropdown from '../../../../components/Dropdown';
import SearchableProjectDropdown from '../../../../components/SearchableProjectDropdown';
import TimeInput from '../../../../components/TimeInput';
import DurationInput from '../../../../components/DurationInput';
import { notifyReadOnly, errorMessage } from '../../../../utils/supabaseErrors';
import { addHoursToTime, formatTime, hoursBetween, LunchBreak } from '../../../../utils/timesheetTime';
import {
  EntryEditorDefaults,
  LEAVE_TYPE_OPTIONS,
  LeaveType,
  TimesheetEntry,
  TimesheetEntryInput
} from '../../config';
import { weekDays, dateKey, describeOption, entryLabel } from '../../timesheetDerive';

interface EntryEditorModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  entry?: TimesheetEntry | null;
  defaults?: EntryEditorDefaults | null;
  weekEndingDate: string;
  /** The lunch break that applies on a day of this week. */
  lunchFor: (date: string) => LunchBreak;
  onClose: () => void;
  onSave: (payload: TimesheetEntryInput) => Promise<void>;
}

type Kind = 'project' | 'leave';

interface FormState {
  kind: Kind;
  projectId: number | null;
  isStage: boolean;
  /** How the picked project reads in the closed dropdown; the id alone cannot say. */
  projectLabel: string | null;
  leaveType: LeaveType;
  date: string;
  start: string | null;
  end: string | null;
  hours: number | null;
  description: string;
}

/**
 * The one entry form: project or leave, day, optional start/end, hours,
 * notes. Opened from the list, the day view, and a grid cell the grid will
 * not edit inline. When both times are set the hours follow them.
 */
const EntryEditorModal: React.FC<EntryEditorModalProps> = ({
  isOpen,
  mode,
  entry,
  defaults,
  weekEndingDate,
  lunchFor,
  onClose,
  onSave
}) => {
  const [form, setForm] = useState<FormState>(() => initial(entry, defaults, weekEndingDate));
  const [errors, setErrors] = useState<{ project?: string; date?: string; hours?: string; general?: string }>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(initial(entry, defaults, weekEndingDate));
      setErrors({});
      setSaving(false);
    }
  }, [isOpen, entry, defaults, weekEndingDate]);

  // Opened from a cell or a chip, the project and day are already known, so
  // the caret goes straight to Hours: type them, Tab, notes, Enter saves.
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      const hours = document.getElementById('entry-hours') as HTMLInputElement | null;
      const target = hours && !hours.disabled ? hours : document.getElementById('entry-notes');
      target?.focus();
    }, 50);
    return () => clearTimeout(t);
  }, [isOpen]);

  const dayItems = useMemo(
    () => weekDays(weekEndingDate).map((d) => ({ id: dateKey(d), value: dateKey(d), label: format(d, 'EEEE d MMM') })),
    [weekEndingDate]
  );

  const timed = Boolean(form.start && form.end);
  // Moving the entry to another day moves it under that day's lunch, so a
  // timed entry's hours are read from its times and the chosen day on every
  // render rather than kept from when the times were typed.
  const lunch = lunchFor(form.date);
  const hours = timed ? hoursBetween(form.start as string, form.end as string, lunch) ?? form.hours : form.hours;

  const setTimes = (start: string | null, end: string | null) => {
    const hours = start && end ? hoursBetween(start, end, lunch) : form.hours;
    setForm((f) => ({ ...f, start, end, hours: hours ?? f.hours }));
  };

  const setHours = (hours: number | null) => {
    // With a start but no end, a duration fixes the end.
    if (form.start && !form.end && hours) {
      setForm((f) => ({ ...f, hours, end: addHoursToTime(f.start as string, hours, lunch) }));
      return;
    }
    setForm((f) => ({ ...f, hours }));
  };

  const handleSave = async () => {
    const next: typeof errors = {};
    if (form.kind === 'project' && !form.projectId) next.project = 'Pick a project or stage.';
    if (!form.date) next.date = 'Pick a day.';
    if (!hours || hours <= 0) next.hours = 'Enter the hours, or a start and end time.';
    if (form.start && form.end && !hoursBetween(form.start, form.end, lunch)) next.hours = 'No time is worked between these times (the lunch break is not worked).';
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setSaving(true);
    setErrors({});
    try {
      await onSave({
        project_id: form.kind === 'project' ? form.projectId : null,
        is_stage: form.kind === 'project' ? form.isStage : null,
        leave_type: form.kind === 'leave' ? form.leaveType : null,
        date: form.date,
        hours: hours as number,
        description: form.description.trim() || null,
        start_time: form.start && form.end ? form.start : null,
        end_time: form.start && form.end ? form.end : null
      });
      onClose();
    } catch (err) {
      console.error('Error saving timesheet entry:', err);
      notifyReadOnly(err);
      setErrors({ general: errorMessage(err, 'The entry could not be saved.') });
    } finally {
      setSaving(false);
    }
  };

  // Enter saves from any field. The field's own handler commits first (it
  // blurs), React re-renders, and only then is the save read through a ref,
  // because this closure's form is from before the commit.
  const latestSave = useRef(handleSave);
  latestSave.current = handleSave;
  const saveOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      setTimeout(() => latestSave.current(), 0);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'edit' ? 'Edit time entry' : 'Add time entry'}
      size="sm"
      className="form-ui entry-editor"
      hasFooter
      footerContent={
        <>
          <button type="button" className="pill-btn pill-btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="pill-btn pill-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Add entry'}
          </button>
        </>
      }
    >
      <div className="entry-editor__body">
        {errors.general && (
          <p className="field__error" role="alert">
            {errors.general}
          </p>
        )}

        {mode === 'create' && (
          <SegmentedControl<Kind>
            options={[
              { value: 'project', label: 'Project work' },
              { value: 'leave', label: 'Leave' }
            ]}
            value={form.kind}
            onChange={(v) => v && setForm((f) => ({ ...f, kind: v }))}
            size="sm"
            fullWidth
            aria-label="Entry type"
          />
        )}

        {form.kind === 'project' ? (
          <Field label="Project or stage" required error={errors.project}>
            <SearchableProjectDropdown
              value={form.projectId}
              displayLabel={form.projectLabel}
              onChange={(id, option) => {
                const described = option ? describeOption(option) : null;
                setForm((f) => ({
                  ...f,
                  projectId: id ? Number(id) : null,
                  isStage: option?.entity_type === 'stage',
                  projectLabel: described ? `${described.code} · ${described.name}` : null
                }));
              }}
              placeholder="Search by job number or name…"
              disabled={saving}
            />
          </Field>
        ) : (
          <Field label="Leave type" required>
            <Dropdown
              items={LEAVE_TYPE_OPTIONS.map((o) => ({ id: o.value, value: o.value, label: o.label }))}
              value={form.leaveType}
              onChange={(v) => setForm((f) => ({ ...f, leaveType: v as LeaveType }))}
              searchable={false}
              disabled={saving}
            />
          </Field>
        )}

        <Field label="Day" required error={errors.date}>
          <Dropdown
            items={dayItems}
            value={form.date}
            onChange={(v) => setForm((f) => ({ ...f, date: v }))}
            placeholder="Pick a day"
            searchable={false}
            disabled={saving}
          />
        </Field>

        <div className="pdm-grid pdm-grid--3">
          <Field label="Start" htmlFor="entry-start" hint={!form.start ? 'Optional' : undefined}>
            <TimeInput
              id="entry-start"
              size="md"
              value={form.start}
              onCommit={(v) => setTimes(v, form.end)}
              disabled={saving}
              aria-label="Start time"
            />
          </Field>
          <Field label="End" htmlFor="entry-end" hint={!form.end ? 'Optional' : undefined}>
            <TimeInput
              id="entry-end"
              size="md"
              value={form.end}
              onCommit={(v) => setTimes(form.start, v)}
              disabled={saving}
              aria-label="End time"
            />
          </Field>
          <Field label="Hours" htmlFor="entry-hours" required error={errors.hours} hint={timed ? (lunch.minutes > 0 ? 'From the times, less lunch' : 'From the times') : undefined}>
            <DurationInput
              id="entry-hours"
              size="md"
              value={hours}
              onCommit={setHours}
              disabled={saving || timed}
              aria-label="Hours"
              onKeyDown={saveOnEnter}
            />
          </Field>
        </div>

        <Field label="Notes" htmlFor="entry-notes">
          <TextArea
            id="entry-notes"
            rows={2}
            autoGrow
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            // Enter saves; Shift+Enter is the way to a second line.
            onKeyDown={saveOnEnter}
            placeholder={form.kind === 'leave' ? 'Anything to note about this leave' : 'What you worked on'}
            disabled={saving}
          />
        </Field>
      </div>
    </Modal>
  );
};

function initial(entry: TimesheetEntry | null | undefined, defaults: EntryEditorDefaults | null | undefined, weekEndingDate: string): FormState {
  if (entry) {
    const label = entryLabel(entry);
    return {
      kind: entry.leave_type ? 'leave' : 'project',
      projectId: entry.project_id,
      isStage: entry.project_type === 'stage',
      projectLabel: entry.leave_type ? null : `${label.code ?? ''} · ${label.name}`,
      leaveType: entry.leave_type ?? 'personal',
      date: entry.date,
      start: entry.start_time ? formatTime(entry.start_time) : null,
      end: entry.end_time ? formatTime(entry.end_time) : null,
      hours: Number(entry.hours),
      description: entry.description ?? ''
    };
  }
  const group = defaults?.group ?? null;
  const option = defaults?.projectOption ?? null;
  const leave = defaults?.leaveType ?? group?.leaveType ?? null;
  const todayKey = dateKey(new Date());
  const days = weekDays(weekEndingDate).map(dateKey);
  const described = option ? describeOption(option) : group && !group.isLeave ? { code: group.jobNumber ?? '', name: group.projectName } : null;
  return {
    kind: leave ? 'leave' : 'project',
    projectId: option ? Number(option.id) : group?.projectId ?? null,
    isStage: option ? option.entity_type === 'stage' : group?.entityType === 'stage',
    projectLabel: described ? `${described.code} · ${described.name}` : null,
    leaveType: leave ?? 'personal',
    date: defaults?.date ?? (days.includes(todayKey) ? todayKey : days[0] ?? ''),
    start: defaults?.startTime ?? null,
    end: null,
    hours: null,
    description: ''
  };
}

export default EntryEditorModal;
